import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { userSelector } from "store/user";
import { SUBSCRIPTION_NAMES } from "utils/consts";
import { STRIPE_PUBLISHABLE_KEY } from "utils/config";
import { formatLongDate } from "utils/format-date";
import { Card, Typography, Button } from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";

const SubscriptionBanners = () => {
    const { subscriptionData: subscription, creditsData, orgStats, userData } = useSelector(userSelector);
    const navigate = useNavigate();

    if (!creditsData || !orgStats || !userData) return null;

    const isMainAdmin = Boolean(userData?.is_main_admin);
    const canPurchase = isMainAdmin && STRIPE_PUBLISHABLE_KEY;

    const creditsRemaining = Number(creditsData?.credits_remaining ?? 0);
    const creditsPurchased = Number(creditsData?.credits_purchased ?? 0);
    const unpaidCount = Number(orgStats?.stats?.respondents_unpaid ?? 0);

    // Free accounts (no purchases) fall back to fixed thresholds below.
    const baseAllowance = creditsPurchased > 0
        ? creditsPurchased
        : Number(creditsData?.recurring_credits_purchased ?? 0);

    const lowCreditThreshold = baseAllowance > 0 ? Math.floor(baseAllowance * 0.15) : 100;
    const isLowCredits = creditsRemaining > 0 && creditsRemaining <= lowCreditThreshold;

    const highUnpaidThreshold = baseAllowance > 0 ? Math.floor(baseAllowance * 0.25) : 25;
    const isHighUnpaid = unpaidCount >= highUnpaidThreshold && baseAllowance > 0;

    const isZeroCredits = creditsRemaining === 0;
    const hasUnpaid = unpaidCount > 0;

    // No plan-change branch: no endpoint has ever returned a
    // pending_plan_change field (plan scheduling does not exist in the
    // backend), so that banner was unreachable and is removed. If plan
    // scheduling is ever built, the banner comes back WITH its field.
    const getSubscriptionBannerMessage = () => {
        if (!subscription) return null;
        if (subscription.cancelled_at) {
            return `Your subscription will be cancelled on ${formatLongDate(
                subscription.cancelled_at
            )}, and your account will be converted to the ${SUBSCRIPTION_NAMES.FREE_INDIVIDUAL}.`;
        }
        return null;
    };

    // Most severe credit-warning tier wins. Mirrors the backend
    // classify_credit_state: HIGH_UNPAID (zero credits + unpaid >= 25%
    // of allowance) auto-pauses surveys via CreditAlerts; UNPAID (zero
    // credits + some unpaid) does not. Team members see escalation copy
    // and no Buy top-up button.
    const getCreditWarningBanner = () => {
        if (isZeroCredits && isHighUnpaid) {
            const held = `${unpaidCount} ${unpaidCount === 1 ? "response is" : "responses are"} being held awaiting credit`;
            return {
                color: "red",
                iconColor: "text-red-500",
                bgColor: "bg-red-50",
                borderColor: "border-red-500",
                buttonColor: "bg-red-500 hover:bg-red-600",
                textColor: "text-red-800",
                message: isMainAdmin
                    ? `Your response credit balance is zero. ${held}, and your surveys have been paused.`
                    : `Your response credit balance is zero. ${held}, and your surveys have been paused. Ask your account admin to purchase credits to release them and resume your surveys.`,
            };
        }
        if (isZeroCredits && hasUnpaid) {
            const held = `${unpaidCount} ${unpaidCount === 1 ? "response is" : "responses are"} being held awaiting credit`;
            return {
                color: "red",
                iconColor: "text-red-500",
                bgColor: "bg-red-50",
                borderColor: "border-red-500",
                buttonColor: "bg-red-500 hover:bg-red-600",
                textColor: "text-red-800",
                message: isMainAdmin
                    ? `Your response credit balance is zero. ${held}.`
                    : `Your response credit balance is zero. ${held}. Ask your account admin to purchase credits to release them.`,
            };
        }
        if (isZeroCredits) {
            return {
                color: "yellow",
                iconColor: "text-yellow-600",
                bgColor: "bg-yellow-50",
                borderColor: "border-yellow-500",
                buttonColor: "bg-yellow-500 hover:bg-yellow-600",
                textColor: "text-yellow-800",
                message: isMainAdmin
                    ? "You have no credits remaining. Purchase credits to continue collecting responses."
                    : "You have no credits remaining. Ask your account admin to purchase credits so the organisation can continue collecting responses.",
            };
        }
        if (isLowCredits) {
            return {
                color: "orange",
                iconColor: "text-orange-500",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-500",
                buttonColor: "bg-orange-500 hover:bg-orange-600",
                textColor: "text-orange-800",
                message: isMainAdmin
                    ? "Your response credit balance is getting low, maybe time for a top-up."
                    : "Your response credit balance is getting low. Ask your account admin about a top-up.",
            };
        }
        return null;
    };

    const subscriptionMessage = getSubscriptionBannerMessage();
    const creditWarning = getCreditWarningBanner();

    if (!subscriptionMessage && !creditWarning) {
        return null;
    }

    const CreditWarningBanner = ({ config }) => (
        <Card className={`mt-2 mb-1 w-full ${config.bgColor} border-l-4 ${config.borderColor} p-4`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                    <MaterialIcon
                        name="warning"
                        className={`mr-3 ${config.iconColor}`}
                        size="lg"
                    />
                    <Typography variant="small" className={`font-body ${config.textColor} font-semibold`}>
                        {config.message}
                    </Typography>
                </div>
                {!!canPurchase && (
                    <Button
                        size="sm"
                        className={`text-white shrink-0 ${config.buttonColor}`}
                        onClick={() => navigate('/top-ups')}
                    >
                        Buy top-up now
                    </Button>
                )}
            </div>
        </Card>
    );

    return (
        <>
            {subscriptionMessage && (
                <Card className="mb-4 mt-4 w-full bg-blue-50 p-4">
                    <div className="flex items-center">
                        <MaterialIcon
                            name="info"
                            className="mr-3 text-blue-500"
                            size="lg"
                        />
                        <Typography variant="small" className="font-body text-blue-700">
                            {subscriptionMessage}
                        </Typography>
                    </div>
                </Card>
            )}
            {creditWarning && <CreditWarningBanner config={creditWarning} />}
        </>
    );
};

export default SubscriptionBanners;
